import * as React from 'react'
import type { PasswordStrength } from 'types/business'
import PasswordStrengthIndicator from 'components/common/password-strength-indicator'
import Row from 'components/common/row'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import InlineError from 'components/ui/inline-error'
import TextField from 'components/ui/text-field'
type Props = {
  password: string
  passwordConfirmation: string
  passwordChangeInProgress?: boolean
  onSubmit: () => unknown
  onPasswordChange: any
  onPasswordConfirmationChange: any
  passwordStrength: PasswordStrength
  errors: Record<string, React.ReactNode | null | undefined>
}

/**
 * Password change form for welcome/account creation flow
 * Includes password confirmation, terms acceptance, and account creation button
 * @param password - New password value
 * @param passwordConfirmation - Password confirmation value
 * @param passwordChangeInProgress - Whether password change is in progress
 * @param onSubmit - Callback when form is submitted
 * @param onPasswordChange - Callback when password input changes
 * @param onPasswordConfirmationChange - Callback when confirmation input changes
 * @param passwordStrength - Business password strength requirements
 * @param errors - Error messages for form validation
 */
const ChangePasswordWelcomeForm = (props: Props): React.ReactElement<'div'> => {
  const submitEnabled =
    props.password &&
    props.passwordConfirmation &&
    !props.errors['re-enter-new-password'] &&
    !props.errors['new-password']
  return (
    <div className="ChangePasswordWelcomeForm">
      <Row columnsSize={[12]} marginBottom="small">
        <div>
          <TextField
            label="Choose a new password"
            type="password"
            autoComplete="new-password"
            onChange={props.onPasswordChange}
            value={props.password}
            invalid={!!props.errors['new-password']}
            error={props.errors['new-password']}
          />
          <PasswordStrengthIndicator
            password={props.password}
            passwordStrength={props.passwordStrength}
            error={props.errors['weak-password']}
          />
        </div>
      </Row>
      <Row columnsSize={[12]} marginBottom="small">
        <div>
          <TextField
            label="Re-enter password"
            type="password"
            autoComplete="new-password"
            onChange={props.onPasswordConfirmationChange}
            value={props.passwordConfirmation}
            invalid={!!props.errors['re-enter-new-password']}
            error={props.errors['re-enter-new-password']}
          />
        </div>
      </Row>
      <Row columnsSize={[12]} marginBottom="small">
        <div>
          <div className="PostInvites_TermsContainer">
            By clicking <b>Create account</b> you agree to be bound by our{' '}
            <a
              className="Login_Link"
              href="https://disco.ac/terms"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms
            </a>{' '}
            and{' '}
            <a
              className="Login_Link"
              href="https://disco.ac/security"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </Row>
      <InlineError error={props.errors['current-password']} />
      <ButtonContainer vertical>
        <Button
          disabled={!submitEnabled}
          large
          appearance="primary"
          caption="Create account"
          progressCaption="Creating account"
          progressIndicator={props.passwordChangeInProgress}
          onClick={props.onSubmit}
        />
      </ButtonContainer>
    </div>
  )
}

export default ChangePasswordWelcomeForm
